/** @jsx h */
import { h } from 'preact'
import groupBy from 'lodash.groupby'
import CardStore from './card-store'
import Step from './step'
import Button from './button'
import { useLabel } from '../use-label'
import Select from './select'
import { useEffect, useMemo, useState } from 'preact/compat'
import { getBoutiques } from '../api'
import { useGlobal } from '../use-global'
import { EXTERNAL_POINT_OF_SALE } from '../utils'
import clsx from 'clsx'
import Spin from './icons/spin'

const PAGE_SIZE = 10

const CardStepBoutique = ({ isOpen, isDone, isEnabled, isHighlighted, onToggle, conf, selectorClassName }) => {
  const t = useLabel()
  const [{ canvasConfig, selectedBoutique, selectedCountry, preselectedBoutiqueId, toggledBoutiqueId }, dispatch] =
    useGlobal()

  const [boutiques, setBoutiques] = useState([])
  const [page, setPage] = useState(1)
  const [allCities, setAllCities] = useState([])
  const [cities, setCities] = useState([])
  const [city, setCity] = useState()
  const [states, setStates] = useState([])
  const [state, setState] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [preselectedBoutique, setPreselectedBoutique] = useState(null)

  const countryName = conf.enabledCountries.find(country => country.iso2Code === selectedCountry)?.name

  const setSelectedCountry = (code, label) => {
    dispatch({ type: 'set-country', payload: { code, label } })
    dispatch({ type: 'reset-toggled-boutique-id' })
  }

  const bTranslations = b => {
    return (
      b.translations?.find(t => t.language.substring(0, 2) === canvasConfig.locale) ||
      b.translations?.find(t => t.isPrimary)
    )
  }

  useEffect(() => {
    if (selectedCountry) {
      setIsLoading(true)
      setBoutiques([])
      getBoutiques({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        maisonId: canvasConfig.maisonId,
        country: selectedCountry,
        language: canvasConfig.locale
      })
        .then(data => {
          const boutiquesData = data
            .filter(el => {
              if (
                conf.configurations.displayExternalBoutiques
                  ? el.boutiqueType === EXTERNAL_POINT_OF_SALE && !el.displayedOnCanvas
                  : el.boutiqueType === EXTERNAL_POINT_OF_SALE
              ) {
                return false
              }

              return true
            })
            .map(boutique => {
              const mappedBoutique = {
                ...boutique,
                additionalHoursText: bTranslations(boutique)?.additionalHoursText || boutique.additionalHoursText || '',
                commercialName: bTranslations(boutique)?.commercialBoutiqueName || boutique.commercialName || '',
                city: bTranslations(boutique)?.shippingCity || boutique.city || '',
                shippingCountry: bTranslations(boutique)?.shippingCountry || boutique.shippingCountry || '',
                postalCode: bTranslations(boutique)?.shippingPostalCode || boutique.postalCode || '',
                shippingState: bTranslations(boutique)?.shippingState || boutique.shippingState || '',
                street: bTranslations(boutique)?.shippingStreet || boutique.street || ''
              }

              delete mappedBoutique.translations
              return mappedBoutique
            })
            .sort((a, b) => (a.city || '').localeCompare(b.city || ''))
            .sort((a, b) => (a.boutiqueType === 'Internal Boutique' ? -1 : 0))

          if (!preselectedBoutiqueId) {
            setBoutiques(boutiquesData)
            return
          }

          const externalBoutique = boutiquesData.find(el => {
            if (el.countryOfOrigin !== selectedCountry) {
              return false
            }

            return el.boutiqueType === EXTERNAL_POINT_OF_SALE
              ? el.externalId?.slice(-6) === preselectedBoutiqueId
              : el.externalId?.split('_')[1] === preselectedBoutiqueId
          })

          if (externalBoutique) {
            if (selectedCountry === 'US' || selectedCountry === 'CA' || selectedCountry === 'JP') {
              setState(externalBoutique.shippingState?.toLowerCase())
            }

            if (selectedCountry !== 'JP') {
              setCity(externalBoutique.city?.toLowerCase())
            }

            setBoutiques([externalBoutique, ...boutiquesData.filter(el => el.id !== externalBoutique.id)])
            setPreselectedBoutique(externalBoutique)
          } else {
            setBoutiques(boutiquesData)
          }
        })
        .catch(console.error)
        .finally(() => setIsLoading(false))
    }
  }, [selectedCountry, canvasConfig, preselectedBoutiqueId, conf])

  // Filtering cities/prefectures and setting those
  useEffect(() => {
    if (!boutiques) return

    const citiesSeen = new Set()
    const uniqueCities = boutiques
      .map(b => ({
        value: b.city.toLowerCase(),
        label: b.city
      }))
      .filter(el => {
        const duplicated = citiesSeen.has(el.value)
        citiesSeen.add(el.value)
        return !duplicated
      })
      .sort((a, b) => (a.label || a.value || '').localeCompare(b.label || b.value || ''))

    setAllCities(uniqueCities)
    setCities(uniqueCities)

    if (selectedCountry === 'US' || selectedCountry === 'CA' || selectedCountry === 'JP') {
      const statesSeen = new Set()

      const uniqueStates = boutiques
        .map(b => ({
          value: b.shippingState.toLowerCase(),
          label: b.shippingState
        }))
        .filter(el => {
          const duplicated = statesSeen.has(el.value)
          statesSeen.add(el.value)
          return !duplicated
        })
        .sort((a, b) => (a.label || a.value || '').localeCompare(b.label || b.value || ''))

      setStates(uniqueStates)
    }
  }, [boutiques, selectedCountry])

  // If there is only 1 city/prefecture then select that one
  useEffect(() => {
    if (!city && cities.length === 1) setCity(cities[0].value)
    if (!state && states.length === 1) setState(states[0].value)
  }, [cities, states, city, state])

  let filteredBoutiques = useMemo(() => [...boutiques], [boutiques])

  if (state && city) {
    filteredBoutiques = boutiques.filter(b => b.shippingState.toLowerCase() === state && b.city.toLowerCase() === city)
  } else if (state && (selectedCountry === 'JP' || selectedCountry === 'US' || selectedCountry === 'CA')) {
    filteredBoutiques = boutiques.filter(b => b.shippingState.toLowerCase() === state)
  } else if (city) {
    filteredBoutiques = boutiques.filter(b => b.city.toLowerCase() === city)
  }

  const filtered = useMemo(
    () => groupBy(filteredBoutiques.slice(0, PAGE_SIZE * page), selectedCountry === 'JP' ? 'shippingState' : 'city'),
    [filteredBoutiques, page, selectedCountry]
  )

  return (
    <Step
      isOpen={isOpen}
      isDone={isDone}
      isEnabled={isEnabled}
      isHighlighted={isHighlighted}
      onToggle={() => {
        isEnabled && onToggle(0)
      }}
      className={selectorClassName}
      index={0}
      title={t('Select_a_Boutique.SB_Select_A_Boutique')}
      summary={selectedBoutique.commercialName || selectedBoutique.name || ''}
    >
      <Select
        id='boutique_locations'
        onChange={e => {
          setPage(1)
          setBoutiques([])
          setCities([])
          setStates([])
          setCity(null)
          setState(null)
          setSelectedCountry(
            e.target.value,
            conf.enabledCountries
              .map(e => ({
                label: e.name,
                value: e.iso2Code
              }))
              .find(el => el.label === e.target.value)
          )
        }}
        label={
          canvasConfig.locale === 'ar' ? `* ${t('Select_a_Boutique.SB_City')}` : `${t('Select_a_Boutique.SB_City')} *`
        }
        value={selectedCountry}
        options={conf.enabledCountries
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map(e => ({
            label: e.name,
            value: e.iso2Code
          }))}
      />
      {(selectedCountry === 'US' || selectedCountry === 'CA') && (
        <Select
          id='boutique_states'
          className='rcc-mt-6'
          disabled={states.length === 0}
          onChange={e => {
            dispatch({ type: 'reset-toggled-boutique-id' })
            setState(e.target.value)
            setCity(null)
            setCities(() => {
              if (!e.target.value) {
                return allCities
              }

              const citiesSeen = new Set()

              return boutiques
                .filter(b => b.shippingState.toLowerCase() === e.target.value)
                .map(b => ({ value: b.city.toLowerCase(), label: b.city }))
                .filter(el => {
                  const duplicated = citiesSeen.has(el.value)
                  citiesSeen.add(el.value)
                  return !duplicated
                })
                .sort((a, b) => (a.label || a.value || '').localeCompare(b.label || b.value || ''))
            })
          }}
          label={t('Select_a_Boutique.SB_State_Sel')}
          value={state}
          options={states}
        />
      )}
      {selectedCountry === 'JP' ? (
        <Select
          id='boutique_states'
          className='rcc-mt-6'
          disabled={states.length === 0}
          onChange={e => {
            dispatch({ type: 'reset-toggled-boutique-id' })
            setState(e.target.value)
          }}
          label={t('Select_a_Boutique.SB_State_Sel')}
          value={state}
          options={states}
        />
      ) : (
        <Select
          id='boutique_cities'
          className='rcc-mt-6'
          disabled={cities.length === 0}
          onChange={e => {
            dispatch({ type: 'reset-toggled-boutique-id' })
            setCity(e.target.value)
          }}
          label={t('Select_a_Boutique.SB_City_Sel')}
          placeholder={canvasConfig.locale === 'ar' && t('Select_a_Boutique.SB_The_City_Placeholder')}
          value={city}
          options={cities}
        />
      )}
      {boutiques.length > 0 && (
        <div
          className={clsx(
            'rcc-flex rcc-justify-between rcc-py-6 rcc-mt-4 rcc-items-center rcc-border-b rcc-border-gray-very-light',
            {
              'rcc-flex-row-reverse': canvasConfig.direction === 'rtl'
            }
          )}
        >
          <p className='rcc-font-extralight rcc-text-base rcc-text-text' role='status' aria-live='polite'>
            {canvasConfig.locale === 'ar'
              ? t('Select_a_Boutique.SB_Our_Boutiques_In', {
                  location:
                    cities.find(c => c.value === city)?.label ||
                    states.find(s => s.value === state)?.label ||
                    countryName
                })
              : t(
                  filteredBoutiques.length === 1
                    ? 'Select_a_Boutique.SB_Summary_Singular'
                    : 'Select_a_Boutique.SB_Summary',
                  {
                    count: filteredBoutiques.length,
                    location:
                      cities.find(c => c.value === city)?.label ||
                      states.find(s => s.value === state)?.label ||
                      countryName
                  }
                )}
          </p>
        </div>
      )}

      {isLoading && (
        <div className='rcc-flex rcc-justify-center rcc-items-center rcc-py-6'>
          <Spin />
        </div>
      )}

      {Object.keys(filtered).length > 0 && (
        <div className='list-store'>
          {Object.keys(filtered).map(el =>
            filtered[el].map(el => (
              <CardStore
                key={el.id}
                boutiqueData={el}
                selectedCountry={selectedCountry}
                toggled={(preselectedBoutique && el.id === preselectedBoutique.id) || toggledBoutiqueId === el.id}
                isPreSelectedBoutique={preselectedBoutique && el.id === preselectedBoutique.id}
                onToggle={() => {
                  dispatch({
                    type: 'set-toggled-boutique',
                    payload: toggledBoutiqueId === el.id ? null : el.id
                  })
                }}
              />
            ))
          )}
          {filteredBoutiques.length > page * PAGE_SIZE && (
            <Button
              className='rcc-w-auto rcc-mt-6 rcc-mx-auto'
              onClick={() => {
                setPage(page => page + 1)
              }}
            >
              {t('Select_a_Boutique.SB_LM')}
            </Button>
          )}
        </div>
      )}
    </Step>
  )
}

export default CardStepBoutique
